import type { TwentyState } from "../hooks/use-twenty";
import { TimerModule } from "../../../ui/molecules/TimerModule";

interface TwentyTimerProps {
  readonly state: TwentyState;
  readonly onStart: () => void;
  readonly onPause: () => void;
  readonly onReset: () => void;
  readonly onSkip: () => void;
  readonly onSettings: () => void;
}

const MODE_LABELS: Record<TwentyState["mode"], string> = {
  screen: "Écran",
  break: "Regardez au loin",
};

const MODE_COLORS: Record<TwentyState["mode"], string> = {
  screen: "text-accent",
  break: "text-success",
};

const MAX_DOTS = 8;

function CycleDots({ count }: { readonly count: number }) {
  if (count <= 0) return null;
  const dots = Math.min(count, MAX_DOTS);
  const overflow = count > MAX_DOTS;

  return (
    <div className="flex items-center gap-1 mt-0.5">
      {Array.from({ length: dots }).map((_, i) => (
        <div key={i} className="w-1.5 h-1.5 rounded-full bg-success" />
      ))}
      {overflow && (
        <span className="text-[10px] text-ink-muted font-mono">+{count - MAX_DOTS}</span>
      )}
    </div>
  );
}

function formatTimeDisplay(timeLeft: number): string {
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function TwentyTimer({
  state,
  onStart,
  onPause,
  onReset,
  onSkip,
  onSettings,
}: TwentyTimerProps) {
  return (
    <TimerModule
      isRunning={state.isRunning}
      timeDisplay={formatTimeDisplay(state.timeLeft)}
      timeColorClass={MODE_COLORS[state.mode]}
      onStart={onStart}
      onPause={onPause}
      onReset={onReset}
      onSettings={onSettings}
      settingsLabel="Réglages 20-20-20"
      info={
        <>
          <span className="text-xs font-semibold text-ink-muted uppercase tracking-widest">
            20-20-20
          </span>
          <span className={`text-xs font-medium ${MODE_COLORS[state.mode]}`}>
            {MODE_LABELS[state.mode]}
          </span>
          <CycleDots count={state.counter} />
        </>
      }
      extraActions={[
        {
          label: "⏭",
          ariaLabel: "Sauter",
          onClick: onSkip,
        },
      ]}
    />
  );
}
